/**
 * lightbox - the GLightbox engine wiring (instance lifecycle, attribute
 * mapping, local Plyr delivery). The public surface lives in index.js.
 */

// Per-slide attributes we translate onto GLightbox's data-* keys. `gallery`
// is handled separately (grouping); everything else maps 1:1.
const SLIDE_ATTRS = ['title', 'description', 'type', 'width', 'height', 'alt', 'effect'];

const SELECTOR = '[lightbox], [data-lightbox]';

let shared = null;      // the page's GLightbox instance (auto-init)
let pageOptions = {};   // extra GLightbox options from g.lightbox({...})
let solo = 0;           // unique gallery counter for ungrouped items

// The published asset base, derived from the tags already on the page (g.init
// does not carry it): the glightbox vendor script first, our own lib script as
// the fallback. Plyr rides the same vendors/ folder (build service vendors.js).
function assetBase() {
	for (const probe of ['script[src*="/vendors/glightbox/"]', 'script[src*="/libs/lightbox/"]']) {
		const tag = document.querySelector(probe);
		if (tag) {
			const src = tag.getAttribute('src');
			return src.split(/\/(?:vendors|libs)\//)[0];
		}
	}
	return '/assets';
}

function reduceMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Base GLightbox settings: translated strings, motion preference, and Plyr
// served from OUR published vendors (GLightbox injects these lazily, only when
// a video slide opens — never from its default CDN).
function baseSettings() {
	const t = window.g ? window.g.translate.bind(window.g) : ((s) => s);
	const base = assetBase();
	const none = reduceMotion();
	return {
		openEffect: none ? 'none' : 'zoom',
		closeEffect: none ? 'none' : 'zoom',
		slideEffect: none ? 'none' : 'slide',
		autoplayVideos: !none,
		moreText: t('See more'),
		lightboxHTML: '<div id="glightbox-body" class="glightbox-container" tabindex="-1" role="dialog" aria-hidden="false">'
			+ '<div class="gloader visible"></div>'
			+ '<div class="goverlay"></div>'
			+ '<div class="gcontainer">'
			+ '<div id="glightbox-slider" class="gslider"></div>'
			+ `<button class="gclose gbtn" aria-label="${t('Close')}" data-taborder="3">{closeSVG}</button>`
			+ `<button class="gprev gbtn" aria-label="${t('Previous')}" data-taborder="2">{prevSVG}</button>`
			+ `<button class="gnext gbtn" aria-label="${t('Next')}" data-taborder="1">{nextSVG}</button>`
			+ '</div></div>',
		plyr: {
			css: base + '/vendors/plyr/plyr.css',
			js: base + '/vendors/plyr/plyr.min.js',
			config: {
				ratio: '16:9',
				iconUrl: base + '/vendors/plyr/plyr.svg',
				// an instantly-erroring data URI: Plyr only sets it to cancel
				// an in-flight download on destroy — never fetch its CDN
				blankVideo: 'data:video/mp4;base64,',
				fullscreen: { enabled: true, iosNative: true },
				youtube: { noCookie: true, rel: 0 },
				vimeo: { byline: false, portrait: false, title: false, transparent: false },
			},
		},
	};
}

function vendorMissing() {
	if (typeof GLightbox !== 'undefined') return false;
	console.error("[lightbox] GLightbox vendor not loaded on this page - add 'vendors' => ['glightbox'] in the page's route.php");
	return true;
}

// Read a `lightbox-*` (or `data-lightbox-*`) attribute.
function attr(el, name) {
	const bare = el.getAttribute('lightbox-' + name);
	return bare !== null ? bare : el.getAttribute('data-lightbox-' + name);
}

// Translate our attributes onto the data-* keys GLightbox parses: data-gallery
// groups slides (the `lightbox` attribute VALUE; valueless items each get a
// unique group so they open alone), the rest per SLIDE_ATTRS.
export function prep() {
	const els = document.querySelectorAll(SELECTOR);
	for (const el of els) {
		if (!el.hasAttribute('data-gallery')) {
			const group = el.getAttribute('lightbox') || el.getAttribute('data-lightbox') || '';
			el.setAttribute('data-gallery', group !== '' ? group : 'lightbox-solo-' + (++solo));
		}
		for (const name of SLIDE_ATTRS) {
			const v = attr(el, name);
			if (v !== null && !el.hasAttribute('data-' + name)) el.setAttribute('data-' + name, v);
		}
	}
	return els.length;
}

function fire(name, detail) {
	document.dispatchEvent(new CustomEvent('lightbox:' + name, { detail: detail || {} }));
}

function wireEvents(instance) {
	instance.on('open', () => fire('open'));
	instance.on('close', () => fire('close'));
	instance.on('slide_changed', ({ current }) => fire('slide', { index: current ? current.index : null }));
}

// (Re)build the shared instance over everything marked on the page.
export function build(options) {
	if (vendorMissing()) return null;
	if (options) pageOptions = options;
	if (shared) {
		shared.destroy();
		shared = null;
	}
	prep();
	shared = GLightbox(Object.assign(baseSettings(), { selector: SELECTOR }, pageOptions));
	wireEvents(shared);
	return shared;
}

// New/changed marked elements (dynamic content): prep them and reload the
// shared instance's element list.
export function refresh() {
	if (!document.querySelector(SELECTOR)) return;
	if (!shared) {
		build();
		return;
	}
	prep();
	shared.reload();
}

export function instance() {
	return shared;
}

export function destroy() {
	if (shared) {
		shared.destroy();
		shared = null;
	}
}

// Programmatic slideshow from plain data (no DOM elements): an item is
// { href, type?, title?, description?, width?, height?, content? } — type is
// auto-detected from href when omitted. Ephemeral instance, torn down on close.
export function open(items, startAt) {
	if (vendorMissing()) return null;
	const elements = (Array.isArray(items) ? items : [items]).filter(Boolean);
	if (!elements.length) return null;
	const box = GLightbox(Object.assign(baseSettings(), {
		elements,
		startAt: Math.min(Math.max(startAt || 0, 0), elements.length - 1),
	}));
	wireEvents(box);
	box.on('close', () => setTimeout(() => box.destroy(), 0));
	box.open();
	return box;
}

// Auto-init: a page with marked elements gets its instance without any JS.
// Dynamic lists re-scan on lister:loaded (a DOM event, not a lib dependency).
export function autoInit() {
	const start = () => {
		if (document.querySelector(SELECTOR)) build();
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
	else start();
	document.addEventListener('lister:loaded', () => refresh());
}
